// Value resolution/comparison helpers for `@if` condition evaluation.
// Supports scope refs (`$i`), `var(--x)`, quoted strings, and numeric units.
import type { LuisArrayVariables } from '../../types'
import type { LuisObjectVariables } from '../../types'
import { evaluateLoopPlaceholderExpression } from './placeholder-expression'

export type ConditionValue =
  | { kind: 'boolean'; value: boolean }
  | { kind: 'number'; value: number; unit: string }
  | { kind: 'string'; value: string }

type ConditionNumeric = { value: number; unit: string }

// Resolve one raw condition token into typed value representation.
export function resolveConditionValue(
  token: string,
  scope: Record<string, string>,
  variables: Record<string, string>,
  objectVariables: LuisObjectVariables,
  arrays: LuisArrayVariables,
): ConditionValue {
  const normalized = normalizeScopeToken(token)
  // Resolve quoted literals before shared expression evaluation strips their intent.
  if (isQuotedLiteral(normalized.trim())) {
    const literal = normalized.trim()
    return { kind: 'string', value: literal.slice(1, -1) }
  }

  const raw = resolveConditionTokenValue(normalized, scope, variables, objectVariables, arrays)
  // Resolve quoted literals as string values.
  if (isQuotedLiteral(raw)) {
    return { kind: 'string', value: raw.slice(1, -1) }
  } else if (raw === 'true' || raw === 'false') {
    // Resolve boolean literals.
    return { kind: 'boolean', value: raw === 'true' }
  } else {
    // Continue numeric/string value resolution.
  }

  const numeric = parseNumericLiteral(raw)
  // Resolve numeric literals with optional units.
  if (numeric) {
    return { kind: 'number', value: numeric.value, unit: numeric.unit }
  } else {
    // Return string fallback for non-numeric tokens.
    return { kind: 'string', value: raw }
  }
}

// Compare two resolved values using condition operator.
export function compareConditionValues(
  left: ConditionValue,
  operator: '===' | '!==' | '>=' | '<=' | '>' | '<',
  right: ConditionValue,
): boolean {
  // Handle strict equality checks across resolved values.
  if (operator === '===') {
    return toComparableString(left) === toComparableString(right)
  } else if (operator === '!==') {
    // Handle strict inequality checks across resolved values.
    return toComparableString(left) !== toComparableString(right)
  } else {
    // Continue relational comparison checks.
  }

  const numericPair = resolveComparableNumericPair(left, right)
  // Reject relational comparisons for incompatible/non-numeric values.
  if (!numericPair) {
    return false
  } else if (operator === '>') {
    return numericPair.left > numericPair.right
  } else if (operator === '>=') {
    return numericPair.left >= numericPair.right
  } else if (operator === '<') {
    return numericPair.left < numericPair.right
  } else {
    // Return <= comparison result for final relational operator.
    return numericPair.left <= numericPair.right
  }
}

// Evaluate truthiness for a resolved condition value.
export function isTruthyConditionValue(value: ConditionValue): boolean {
  if (value.kind === 'boolean') {
    return value.value
  } else if (value.kind === 'number') {
    return value.value !== 0
  } else {
    // Return string truthiness result.
    return value.value.length > 0
  }
}

// Normalize `$x` token references into plain scope key names.
function normalizeScopeToken(token: string): string {
  const trimmed = token.trim()
  return trimmed.startsWith('$') ? trimmed.slice(1) : trimmed
}

// Resolve one condition token through direct scope or shared expression evaluation.
function resolveConditionTokenValue(
  token: string,
  scope: Record<string, string>,
  variables: Record<string, string>,
  objectVariables: LuisObjectVariables,
  arrays: LuisArrayVariables,
): string {
  if (token in scope) return scope[token].trim()
  const resolved = evaluateLoopPlaceholderExpression(token.trim(), scope, variables, objectVariables, arrays)
  return resolved ? resolved.trim() : token.trim()
}

// Parse one numeric literal with optional unit suffix.
function parseNumericLiteral(raw: string): ConditionNumeric | null {
  // Regex matches optional-sign numeric tokens with units. Example match: "12px"
  const match = raw.match(/^(-?\d+(?:\.\d+)?)([a-zA-Z%]+)?$/)
  return match ? { value: Number.parseFloat(match[1]), unit: match[2] ?? '' } : null
}

// Convert resolved value to comparable string representation.
function toComparableString(value: ConditionValue): string {
  return value.kind === 'number' ? `${value.value}${value.unit}` : String(value.value)
}

// Resolve numeric pair for relational comparisons with unit-compatibility checks.
function resolveComparableNumericPair(left: ConditionValue, right: ConditionValue): { left: number; right: number } | null {
  const leftNumeric = coerceConditionNumber(left)
  const rightNumeric = coerceConditionNumber(right)
  if (!leftNumeric || !rightNumeric) {
    return null
  } else if (leftNumeric.unit && rightNumeric.unit && leftNumeric.unit !== rightNumeric.unit) {
    return null
  } else {
    return { left: leftNumeric.value, right: rightNumeric.value }
  }
}

// Coerce numeric-looking strings for relational operators without changing equality semantics.
function coerceConditionNumber(value: ConditionValue): ConditionNumeric | null {
  if (value.kind === 'number') return { value: value.value, unit: value.unit }
  return value.kind === 'string' ? parseNumericLiteral(value.value.trim()) : null
}

// Check if value is wrapped with matching single/double quotes.
function isQuotedLiteral(value: string): boolean {
  return (
    (value.length >= 2 && value.startsWith("'") && value.endsWith("'"))
    || (value.length >= 2 && value.startsWith('"') && value.endsWith('"'))
  )
}
